/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef } from "react";
import { useSelector } from "react-redux";
import { useResizeDetector } from "react-resize-detector";
import type { DataTableProps } from "@/types/DataTableTypes";
import { RootState } from "@/types/RootState";
import LoaderSpinner from "@/components/ReactBootstrap/LoaderSpinner/LoaderSpinner";
import NoDataContainer from "@/cleanup/containers/NoData/NoDataContainer";
import { useDataFilterTable } from "@/components/SntTableViewCard/hooks/useDataFilterTable";
import { Styles } from "../../styles/TableStyles";
import { useStickyHeader, useFloatingScroll, useHeaderWidthSync } from "../../hooks";
import { StickyTableHeader } from "./StickyTableHeader";
import { StickyTableBody } from "./StickyTableBody";
import { SntTablePaging } from "../TableFooter/SntTablePaging";

export const StickyTable = ({
  dataFilter,
  data,
  onConfigColumn,
  isHideConfigColumn,
  onFilterClicked,
  loading,
  noDataText,
  isShowSelectPageSize = true,
  isFloatingFooter = true,
  isShowPaging = true,
  isShowDecoratorColor = false,
  isDisabbledGroup = true,
  onRenderConfig,
  onExtraColumnActionClicked,
  isDisabledCopyData = true,
  isDisabbledAddFilter = true,
  restrictedFilters,
  filterVisibleKeys,
  filterMap = {},
}: DataTableProps) => {
  const loginInfo = useSelector((state: RootState) => state.user);

  const defaultColumn = React.useMemo(
    () => ({
      maxWidth: 600,
    }),
    []
  );

  const table = useDataFilterTable(dataFilter || ({} as any), data, {
    defaultColumn: defaultColumn,
  });

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    totalItems,
    toggleSortBy,
    state: { pageIndex, pageSize },
  } = table;

  const { width, ref } = useResizeDetector();
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  // Custom hooks for sticky behavior
  useStickyHeader({
    headerRef,
    containerRef,
  });

  useFloatingScroll({
    containerRef,
    isFloatingFooter,
    pageIndex,
    pageSize,
    width,
    loading: loading || false,
  });

  useHeaderWidthSync({
    containerRef,
    rows,
    loading: loading || false,
  });

  const tableProps = getTableProps();
  tableProps.style = tableProps.style || {};
  const existingMinWidth = parseInt(tableProps.style.minWidth || "0", 10);
  tableProps.style.minWidth = existingMinWidth + 40 + "px";

  const showConfigColumn =
    (!isHideConfigColumn && onConfigColumn) || onRenderConfig;

  return (
    <div ref={containerRef}>
      <Styles>
        <div className="table-container" ref={ref}>
          <div {...tableProps} className="table">
            <div className="full-screen-table" ref={headerRef}>
              <StickyTableHeader
                headerGroups={headerGroups}
                dataFilter={dataFilter}
                rows={rows}
                prepareRow={prepareRow}
                toggleSortBy={toggleSortBy}
                isFloatingFooter={isFloatingFooter}
                containerRef={containerRef}
                isHideConfigColumn={isHideConfigColumn || false}
                onConfigColumn={onConfigColumn}
                onRenderConfig={onRenderConfig}
                isDisabbledGroup={isDisabbledGroup}
                isDisabledCopyData={isDisabledCopyData}
                onExtraColumnActionClicked={onExtraColumnActionClicked}
                isDisabbledAddFilter={isDisabbledAddFilter}
                onFilterClicked={onFilterClicked}
                restrictedFilters={restrictedFilters}
                filterVisibleKeys={filterVisibleKeys}
                organisationType={loginInfo?.organisation?.organisationType}
              />
            </div>

            {rows.length === 0 && !loading ? (
              <></>
            ) : (
              <StickyTableBody
                rows={rows}
                prepareRow={prepareRow}
                getTableBodyProps={getTableBodyProps}
                isShowDecoratorColor={isShowDecoratorColor}
                showConfigColumn={!!showConfigColumn}
                onRenderConfig={onRenderConfig}
                isGroupedMode={table.isGroupedMode}
                isDisabbledAddFilter={isDisabbledAddFilter}
                restrictedFilters={restrictedFilters}
                filterVisibleKeys={filterVisibleKeys}
                filterMap={filterMap}
                onFilterClicked={onFilterClicked}
              />
            )}

            {rows.length === 0 && (
              <NoDataContainer noDataText={noDataText} loading={loading} />
            )}
          </div>
        </div>

        <div style={{ marginBottom: "5px" }}>&nbsp;</div>

        {isShowPaging && (
          <div
            className={`table-footer ${isFloatingFooter ? "floating-footer" : ""}`}
          >
            <div
              style={{
                padding: isFloatingFooter ? "0.5rem" : undefined,
                paddingBottom: isFloatingFooter ? "0px" : undefined,
              }}
            >
              <SntTablePaging
                gotoPage={gotoPage}
                previousPage={previousPage}
                nextPage={nextPage}
                pageCount={pageCount}
                canPreviousPage={canPreviousPage}
                canNextPage={canNextPage}
                pageIndex={pageIndex}
                pageOptions={pageOptions}
                pageSize={pageSize}
                setPageSize={setPageSize}
                isShowSelectPageSize={isShowSelectPageSize}
                totalItems={totalItems}
                headerGroups={headerGroups}
              />
            </div>
          </div>
        )}

        <LoaderSpinner loading={loading} />
      </Styles>
    </div>
  );
};
